/**
 * Stores athlete video File objects in IndexedDB for ephemeral replay.
 * Videos are auto-purged after 24 hours.
 *
 * FUTURE: If SPORTFORM_RETAIN_ALL_VIDEOS is enabled, this layer is still
 * useful for faster local playback, reducing server egress.
 */

const DB_NAME = 'sportform-video-cache';
const STORE_NAME = 'videos';
const DB_VERSION = 1;

interface StoredVideo {
  videoId: string;
  file: File;
  savedAt: number; // Date.now() timestamp
}

export async function openVideoDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'videoId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function storeVideoFile(videoId: string, file: File): Promise<void> {
  try {
    const db = await openVideoDb();
    const entry: StoredVideo = { videoId, file, savedAt: Date.now() };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    db.close();
  } catch {
    // Safari private mode and storage quota errors — fail silently
  }
}

export async function retrieveVideoFile(videoId: string): Promise<File | null> {
  try {
    const db = await openVideoDb();
    const entry = await new Promise<StoredVideo | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(videoId);
      req.onsuccess = () => resolve(req.result as StoredVideo | undefined);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return entry?.file ?? null;
  } catch {
    return null;
  }
}

export async function deleteVideoFile(videoId: string): Promise<void> {
  try {
    const db = await openVideoDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(videoId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    db.close();
  } catch {
    // Fail silently
  }
}

export async function purgeOldEntries(maxAgeMs: number = 24 * 60 * 60 * 1000): Promise<void> {
  try {
    const db = await openVideoDb();
    const cutoff = Date.now() - maxAgeMs;

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();

      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          resolve();
          return;
        }
        const entry = cursor.value as StoredVideo;
        if (entry.savedAt < cutoff) {
          cursor.delete();
        }
        cursor.continue();
      };

      req.onerror = () => reject(req.error);
    });

    db.close();
  } catch {
    // Fail silently
  }
}
